import React from 'react';
import moment from 'moment';
import BaseComponent from 'src/components/BaseComponent';

import { lang, remCss } from 'src/helpers';
import LANG_CONST from 'src/helpers/lang/const';

import { Spinner } from 'src/components/basic';
import SVGIcon from 'src/components/svg';
import AlertManager from 'src/components/alert';

import { Doctor } from './Doctor';
import { Patient } from './Patient';
import { AddPatient } from './AddPatient';
import { AddPatientDetail } from './AddPatientDetail';
import { BookSlots } from './Slots';
import { BookInfo } from './Info';
import { BookOffice } from './Office';

import { BOOK_TYPE, STEPS } from './constants';
import frontBookConfig from './config.front';
import defaultModel from './model';
import defaultAccountModel from './accountModel';

/**
 * The booking wizard. One component for both apps.
 *
 * Props (all optional, they default to the front app):
 *   model, accountModel, config  - see `createBookContext(config)`
 *
 * Admin passes its own context, see
 * `src/pages/admin/main/Appointments/Book/index.js`.
 */
export class BookAppointment extends BaseComponent {
  static defaultProps = {
    config: frontBookConfig,
    model: defaultModel,
    accountModel: defaultAccountModel
  };

  state = {
    loading: true,
    tabs: []
  };

  componentDidMount() {
    const { model } = this.props;
    model.subscribe(this.subscription);
    model.on('accountSwitched', this.accountSwitched);
    model.on('checkSlotError', this.onCheckSlotError);
    this.init().then();
  }

  componentWillUnmount() {
    const { model } = this.props;
    model.unsubscribe(this.subscription);
    model.cancel('accountSwitched', this.accountSwitched);
    model.cancel('checkSlotError', this.onCheckSlotError);
  }

  accountSwitched = () => {
    this.back();
  };

  onCheckSlotError = () => {
    const { model } = this.props;
    const errorMessage = lang.t(LANG_CONST.notice.appointment_taken, {
      dateTime: moment(model.state.selectedSlot.startDate).format('h:mm a[ on ]MMMM DD[, ]YYYY')
    });
    AlertManager.popup(AlertManager.TYPES.WARN, {
      title: errorMessage,
      close: false,
      width: 450,
      actionButton: {
        label: LANG_CONST.buttons.ok,
        action: (actions) => {
          actions.hide();
          this.init().then();
        }
      }
    });
  };

  getSlotProp = () => {
    const { location } = this.props.router;

    return location?.state?.slot;
  };

  getParamsProp = () => {
    const { params } = this.props.router;

    return params || {};
  };

  getBookType = (user, params) => {
    if (params?.rebookId) return BOOK_TYPE.REBOOK;
    if (params?.rescheduleId) return BOOK_TYPE.RESCHEDULE;
    if (this.props.config.roleBasedFlows) {
      if (user?.isPatient) return BOOK_TYPE.PATIENT_BOOK;
      if (user?.isDoctor) return BOOK_TYPE.DOCTOR_BOOK;
    }
    return BOOK_TYPE.CLINIC_BOOK;
  };

  init = async () => {
    const { model, accountModel } = this.props;
    this.setState({ loading: true });
    accountModel.reset();
    model.resetBook();
    const { user } = model.state;
    const params = this.getParamsProp();
    const slotProp = this.getSlotProp();

    let doctor,
      doctorId,
      patientId,
      appointment = null,
      selectedSlot,
      answers = [],
      saveApi = 'createAppointment',
      step = STEPS.SLOTS;

    const type = this.getBookType(user, params);

    switch (type) {
      case BOOK_TYPE.PATIENT_BOOK:
        patientId = user.id;
        model.setStateSilent({ patientId });

        doctor = await model.loadDoctor(params.username);
        doctorId = doctor.id;
        answers = doctor.questions?.map((question) => ({ question, answer: '' }));

        selectedSlot = slotProp || (await model.getAvailableSlots(doctorId));
        break;

      case BOOK_TYPE.DOCTOR_BOOK:
        if (params.patientId) {
          patientId = parseInt(params.patientId);
          model.setStateSilent({ patientId });
        } else step = STEPS.PATIENT;

        doctor = await model.loadDoctor(model.state.user.username);
        doctorId = doctor.id;
        answers = doctor.questions?.map((question) => ({ question, answer: '' }));

        selectedSlot = slotProp || (await model.getAvailableSlots(doctorId));
        break;

      case BOOK_TYPE.REBOOK:
        appointment = await model.loadAppointmentDetail(params.rebookId);

        doctor = appointment.doctor;
        doctorId = appointment.doctor.id;
        answers = doctor.questions?.map((question) => ({ question, answer: '' }));

        patientId = appointment.patient.id;

        selectedSlot = await model.getAvailableSlots(doctorId);
        break;

      case BOOK_TYPE.CLINIC_BOOK:
        step = STEPS.DOCTOR;

        if (slotProp) {
          selectedSlot = slotProp;
          step = STEPS.PATIENT;

          doctorId = selectedSlot.userId;
          doctor = await model.loadDoctor('', doctorId);
          answers = doctor.questions?.map((question) => ({ question, answer: '' }));
        }

        break;

      case BOOK_TYPE.RESCHEDULE:
        appointment = await model.loadAppointmentDetail(params.rescheduleId);
        doctor = appointment.doctor;
        doctorId = appointment.doctor.id;
        answers = appointment.answers;

        patientId = appointment.patient.id;

        selectedSlot = await model.getAvailableSlots(doctorId);
        step = STEPS.SLOTS;
        saveApi = 'rescheduleAppointment';
        break;
    }

    if (doctor && model.checkDisabledDoctor(doctor)) {
      return this.back();
    }

    model.setState({
      doctor,
      doctorId,
      patientId,
      appointment,
      answers: answers || [],
      saveApi,
      step,
      type,
      selectedSlot
    });

    await this.loadSlots();

    this.setState({ loading: false });
  };

  loadSlots = async () => {
    const { model, config } = this.props;
    const { doctorId, patientId, selectedSlot } = model.state;

    if (doctorId && patientId) {
      await model.getSlots(selectedSlot, () => {
        if (config.scrollCalendarOnLoad) setTimeout(() => this.calendarRef?.scrollToDate(), 500);
      });
    }
  };

  loadBeforeSlotStep = async (patient) => {
    const { model } = this.props;
    model.setStateSilent({ patientId: patient.id });
    const slotProp = this.getSlotProp();

    let [selectedSlot, doctor] = await Promise.all([
      slotProp || model.getAvailableSlots(model.state.doctorId),
      model.loadDoctor(model.state.doctor.username)
    ]);

    model.setStateSilent({
      doctor,
      selectedSlot,
      answers: (doctor.questions || [])?.map((question) => ({ question, answer: '' }))
    });
    this.loadSlots().then();
    model.nextStep(STEPS.SLOTS);
  };

  render() {
    const { model, accountModel, config } = this.props;

    if (this.state.loading)
      return (
        <div className={'mc-flex mc-m-auto'}>
          <Spinner />
        </div>
      );

    // props shared by every step
    const stepProps = {
      ...this.props,
      model,
      accountModel,
      config,
      onBack: this.back,
      onDone: config.finishWithBack ? this.back : undefined
    };

    return (
      <div className={'mc-container-content'}>
        <div className={'mc-container-content__header mc-flex-15'}>
          <div
            className={'mc-no-style-btn'}
            style={{ height: remCss(40) }}
            onClick={() => this.back()}
          >
            <SVGIcon width={40} iconname={'back-icon'} />
          </div>
          <span className={'mc-grow'}>
            {lang.t(
              model.state.step === STEPS.OFFICE
                ? LANG_CONST.buttons.make_appointment
                : model.state.type === BOOK_TYPE.RESCHEDULE
                  ? LANG_CONST.buttons.reschedule_appointment
                  : LANG_CONST.buttons.add_new_appointment
            )}
          </span>
        </div>
        <div className={'mc-container-content__body mc-book-doctor'}>
          <div className={'mc-container-content__body-content'}>
            <div className={'mc-tab-box'}>
              <Doctor {...stepProps} />
              <Patient {...stepProps} loadBeforeSlotStep={this.loadBeforeSlotStep} />
              <AddPatient {...stepProps} loadBeforeSlotStep={this.loadBeforeSlotStep} />
              <AddPatientDetail {...stepProps} loadBeforeSlotStep={this.loadBeforeSlotStep} />
              <BookSlots {...stepProps} onCalendarRef={(r) => (this.calendarRef = r)} />
              <BookInfo {...stepProps} />
              <BookOffice {...stepProps} />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

// Kept for compatibility: the front model is still the default export
export default defaultModel;
